import {
  IdcardOutlined,
  LinkOutlined,
  MailOutlined,
  PhoneOutlined,
  UserOutlined,
} from "@ant-design/icons";
import {
  Button,
  Checkbox,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Radio,
  Select,
} from "antd";
import axios from "axios";
import React from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { addNotification } from "../store/slices/notificationSlice";
import "./LoanApplicationForm.scss";

const { Option } = Select;

const LoanApplicationForm: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [form] = Form.useForm();

  const onFinish = (values: any) => {
    axios
      .post("/file-login", values)
      .then(() => {
        dispatch(
          addNotification({
            type: "success",
            message: "New lead created successfully!",
          })
        );
        navigate("/dashboard");
      })
      .catch(({ response }) => {
        dispatch(
          addNotification({
            type: "error",
            message: response.data.description || "An error occurred",
          })
        );
      });
  };

  return (
    <div className="loan-form-container">
      <div className="loan-form-content">
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          requiredMark={false}
          initialValues={{
            residenceType: "INDIAN",
            loanType: "HOME",
            customerProfile: "SALARIED",
          }}
        >
          <Form.Item
            name="name"
            label="Borrower Name"
            rules={[{ required: true, message: "Please enter your full name" }]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="Enter your full name"
              size="large"
            />
          </Form.Item>
          {/* ADD DATE OF BIRTH */}
          <Form.Item
            name="dateOfBirth"
            label="Date of Birth"
            rules={[
              { required: true, message: "Please enter your date of birth" },
            ]}
            style={{ width: "100%" }}
          >
            <DatePicker
              placeholder="Select date of birth"
              size="large"
              format="DD-MM-YYYY"
              style={{ width: "100%" }}
            />
          </Form.Item>
          <Form.Item
            name="panCard"
            label="PAN Card Number"
            rules={[
              {
                required: true,
                message: "Please enter your PAN card number",
              },
              {
                pattern: /^[A-Z]{5}[0-9]{4}[A-Z]$/,
                message: "Please enter a valid PAN card number",
              },
            ]}
          >
            <Input
              prefix={<IdcardOutlined />}
              placeholder="Enter PAN card number"
              size="large"
            />
          </Form.Item>
          <Form.Item
            name="phoneNumber"
            label="Phone Number"
            rules={[
              { required: true, message: "Please enter your phone number" },
              {
                pattern: /^[0-9]{10}$/,
                message: "Please enter a valid 10-digit phone number",
              },
            ]}
          >
            <Input
              prefix={<PhoneOutlined />}
              placeholder="Enter 10-digit phone number"
              size="large"
            />
          </Form.Item>
          {/* Add email */}
          <Form.Item
            name="email"
            label="Email"
            rules={[{ required: true, message: "Please enter your email" }]}
          >
            <Input
              prefix={<MailOutlined />}
              placeholder="Enter email"
              size="large"
            />
          </Form.Item>
          <Form.Item
            name="loanAmount"
            label="Requested Loan Amount (₹)"
            rules={[
              { required: true, message: "Please enter loan amount" },
              {
                type: "number",
                min: 500000,
                max: 10000000,
                message: "Loan amount must be between ₹5 Lac and ₹1 crore",
              },
            ]}
          >
            <InputNumber<number>
              style={{ width: "100%" }}
              formatter={(value) =>
                `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
              }
              placeholder="Enter loan amount"
              size="large"
            />
          </Form.Item>
          {/* Add Residence Type Radio Button */}
          <Form.Item
            name="residenceType"
            label="Residence Type"
            rules={[
              { required: true, message: "Please select residence type" },
            ]}
          >
            <Radio.Group
              block
              optionType="button"
              buttonStyle="solid"
              defaultValue={"INDIAN"}
            >
              <Radio value="INDIAN">Indian Resident</Radio>
              <Radio value="NRI">NRI</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            name="loanType"
            label="Loan Type"
            rules={[{ required: true, message: "Please select loan type" }]}
          >
            <Radio.Group
              block
              optionType="button"
              buttonStyle="solid"
              defaultValue={"HOME"}
            >
              <Radio value="HOME">Home Loan</Radio>
              <Radio value="NON_HOME">Non Home Loan</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            name="loanPurpose"
            label="Loan Purpose"
            rules={[{ required: true, message: "Please select loan purpose" }]}
          >
            <Select placeholder="Select Loan Purpose" size="large">
              <Option value="PURCHASE_HOUSE">Purchase - House</Option>
              <Option value="PURCHASE_FLAT">Purchase - Flat / Floor</Option>
              <Option value="PURCHASE_PLOT">Purchase - Plot Only</Option>
              <Option value="PURCHASE_PLOT_CONSTRUCTION">
                Purchase - Plot + Construction
              </Option>
              <Option value="PURCHASE_SELLER_BT">Purchase - Seller BT</Option>
              <Option value="PURCHASE_NON_RESIDENTIAL_PROPERTY">
                Purchase - Non-Residential Property
              </Option>
              <Option value="CONSTRUCTION_EXTENSION_RENOVATION">
                Construction/Extension/Renovation
              </Option>
              <Option value="BALANCE_TRANSFER_HOME_LOAN">
                Balance Transfer - Home Loan
              </Option>
              <Option value="BALANCE_TRANSFER_HL_TOP_UP">
                Balance Transfer - HL + Top Up
              </Option>
              <Option value="BALANCE_TRANSFER_LAP">
                Balance Transfer - LAP
              </Option>
              <Option value="BALANCE_TRANSFER_LAP_TOP_UP">
                Balance Transfer - LAP + Top Up
              </Option>
              <Option value="LOAN_AGAINST_RESIDENTIAL_PROPERTY">
                Loan Against - Residential Property
              </Option>
              <Option value="LOAN_AGAINST_PLOT">Loan Against - Plot</Option>
              <Option value="LOAN_AGAINST_NON_RESIDENTIAL_PROPERTY">
                Loan Against - Non-Residential Property
              </Option>
            </Select>
          </Form.Item>
          {/* Customer Profile Radio Button Salaried or Self Employed  */}
          <Form.Item
            name="customerProfile"
            label="Customer Profile"
            rules={[
              { required: true, message: "Please select customer profile" },
            ]}
          >
            <Radio.Group
              block
              optionType="button"
              buttonStyle="solid"
              defaultValue={"SALARIED"}
            >
              <Radio value="SALARIED">Salaried</Radio>
              <Radio value="SELF_EMPLOYED">Self Employed</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            name="termsAccepted"
            valuePropName="checked"
            rules={[
              {
                validator: (_, value) =>
                  value
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error("Please accept the terms and conditions")
                      ),
              },
            ]}
          >
            <Checkbox>
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://dwsfinance.com/terms-and-conditions"
              >
                I accept the terms and conditions
                <LinkOutlined />
              </a>
            </Checkbox>
          </Form.Item>
          <Form.Item className="form-actions">
            <Button
              type="default"
              onClick={() => navigate("/dashboard")}
              size="large"
              className="cancel-button"
            >
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              className="submit-button"
            >
              Submit Application
            </Button>
          </Form.Item>
        </Form>
      </div>
    </div>
  );
};

export default LoanApplicationForm;
